import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { 
  Search, 
  Eye, 
  FileText, 
  TrendingUp, 
  MoreVertical, 
  User, 
  CheckSquare, 
  Square,
  ChevronDown,
  Calendar,
  Users,
  GraduationCap,
  X,
  Mail,
  Phone,
  MapPin,
  Droplets,
  Shield,
  UserCheck,
  BookOpen,
  UserPlus,
  UserX,
  Ban,
  Brain,
  MessageSquare,
  Award,
  MessageCircle,
  MessageSquareDot,
  AwardIcon,
  BookA,
  Pencil,
  Sparkles,
  Wand2,
  Loader2,
  Trash,
  // BookOpen
} from 'lucide-react';
// import * as XLSX from 'xlsx';
import * as XLSX from "xlsx-js-style";
import { useNavigate, useLocation } from 'react-router-dom';
import useFetch from '../constants/useFetch';
import { API_BASE_URL } from '../constants';
import Select from 'react-select';
import { FaFileAlt, FaTimes } from 'react-icons/fa';
import { PDFDownloadLink } from '@react-pdf/renderer';
import ClassBatchResultPDF from './pdf/ClassBatchResultPDF';


// Utility to compare arrays for deep equality
const arraysEqual = (arr1, arr2) => {
  if (arr1.length !== arr2.length) return false;
  return arr1.every((item, index) => item === arr2[index]);
};

const Students = () => {
  const { user: userInfo } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const location = useLocation();
  const { teacherData } = useSelector((state) => state.teacherData || {});
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [actionDropdownId, setActionDropdownId] = useState(null);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [showSubjectScoresModal, setShowSubjectScoresModal] = useState(false);
  const [modalData, setModalData] = useState({});
  const [selectedAction, setSelectedAction] = useState(null);
  const [sortOption, setSortOption] = useState('a-z');

  const [isSubjectModalOpen, setIsSubjectModalOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);

  // Batch PDF state
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchResults, setBatchResults] = useState([]);
  const [batchError, setBatchError] = useState(null);
  const [showBatchModal, setShowBatchModal] = useState(false);

  // Get class ID from URL params or use first class as default
  const classId = new URLSearchParams(location.search).get('classId') || 
                  (teacherData?.classrooms?.[0]?.uuid);

  // Fetch students data if not in Redux
  const studentsUrl = `${API_BASE_URL}/api/web/admin/all-students-by-class/${classId}`;
  const { 
    data: studentsData, 
    loading: studentsLoading, 
    error: studentsError, 
    refetch: refetchStudents 
  } = useFetch(studentsUrl, 'studentsData', !teacherData?.students);

  // Use students from Redux if available, otherwise from API
  const students = teacherData?.students || studentsData?.students || [];
  // console.log({students});
  
  const currentClass = teacherData?.classrooms?.find(cls => cls.uuid === classId) || studentsData?.currentClass || {};
  const currentTerm = studentsData?.currentTerm || null;
  const currentSession = studentsData?.currentSession || null;
  const allClasses = studentsData?.classrooms || [];
  const subjects = studentsData?.subjects || [];
  const psychomotorSkills = studentsData?.psychomotors || null;
  const subjectScores = studentsData?.subjectScores || [];
  const termlyScores = studentsData?.termlyScores || [];
  const levelTermlyScores = studentsData?.levelTermlyScores || [];

  const subjectOptions = subjects.map((subject) => ({
    value: subject.subject_name,
    label: subject.subject_name,
  }));

  // console.log({ currentClass, currentSession, currentTerm });
  // console.log({subjects});
  
  // Utility function to format decimal values
const formatScore = (value) => {
  if (value === undefined || value === null || value === '') return 0;
  
  const num = parseFloat(value);
  if (isNaN(num)) return '';
  
  // If the number ends with .0, show as integer
  if (num % 1 === 0) {
    return Math.floor(num);
  }
  
  // Otherwise, show as is
  return num;
};

  // Batch results helpers
  const handleOpenBatchResultsModal = async (studentIds) => {
    if (!studentIds || studentIds.length === 0) {
      alert('Please select at least one student');
      return;
    }

    setBatchError(null);
    setBatchResults([]);
    setShowBatchModal(true);
    setBatchLoading(true);

    try {
      const token = localStorage.getItem('authToken');
      const className = currentClass?.class_name;
      if (!className) {
        throw new Error('Current class name not found.');
      }

      // Resolve UUIDs for selected students
      const selectedStudentObjects = students.filter(s => studentIds.includes(s.id));

      const resultPromises = selectedStudentObjects.map(async (stu) => {
        if (!stu.uuid) return null;
        const res = await fetch(
          `${API_BASE_URL}/api/web/result/student-summary-data/${stu.uuid}/${encodeURIComponent(className)}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const json = await res.json();
        if (!json.success) {
          console.error('Failed to load result for student', stu.uuid, json.message);
          return null;
        }
        return json.data;
      });

      const results = await Promise.all(resultPromises);
      const validResults = results.filter(r => r);
      // console.log({results, validResults})

      if (validResults.length === 0) {
        setBatchError('No result data found for selected students.');
      }

      setBatchResults(validResults);
    } catch (error) {
      console.error('Error loading batch student results:', error);
      setBatchError(error.message || 'Failed to load batch student results.');
    } finally {
      setBatchLoading(false);
    }
  };

  // Function to get positions from score list
  //handling ties(as 1, 1, 2)
//   const getPositions = (scoreList) => {
//     const sorted = [...scoreList].sort((a, b) => (b.score ?? -1) - (a.score ?? -1));
//     const positions = {};
//     let lastScore = null;
//     let lastPosition = 0;
//     let skip = 0;
//     for (let index = 0; index < sorted.length; index++) {
//       const item = sorted[index];
//       if (item.score == null) {
//         positions[item.student_id] = null;
//         continue;
//       }
//       if (item.score === lastScore) {
//         skip++;
//         positions[item.student_id] = lastPosition;
//       } else {
//         lastPosition = index + 1 - skip;
//         positions[item.student_id] = lastPosition;
//         lastScore = item.score;
//         skip = 0;
//       }
//     }
//     return positions;
//   };

  const downloadSample1 = () => {
      if (!students.length) {
        alert("No students loaded. Please load students first.");
        return;
      }

      const classLabel = currentClass.class_name || "";
      const termLabel = currentTerm || "";
      const sessionLabel = currentSession || "";

      // Sheet structure
      const sampleSheetData = [
        [classLabel, termLabel, sessionLabel, ""],
        [], // blank row
        ["Student Name", "Admission No", "", ""],
      ];

      students.forEach(student =>
        sampleSheetData.push([
          `${student.lastname || ""} ${student.firstname || ""} ${student.middlename || ""}`,
          student.admission_no  || "",
          "",
          ""
        ])
      );

      const worksheet = XLSX.utils.aoa_to_sheet(sampleSheetData);

      // Apply styles
      const headerStyle = {
        font: { bold: true, sz: 12 },
        alignment: { horizontal: "center" }
      };

      ["A3", "B3", "C3", "D3", "E3"].forEach(cell => {
        if (worksheet[cell]) worksheet[cell].s = headerStyle;
      });   

      // Column widths
      worksheet["!cols"] = [
        { wch: 30 }, // Admission No
        { wch: 15 }, // Name
        { wch: 10 }, // CA1
        { wch: 10 }, // CA2
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, `${classLabel}-students-list`);

      XLSX.writeFile(
        workbook,
        `${classLabel}-students-list.xlsx`
      );
  };

const downloadSample = () => {
  if (!students.length) {
    alert("No students loaded. Please load students first.");
    return;
  }

  const classLabel = currentClass.class_name || "";
  const termLabel = currentTerm || "";
  const sessionLabel = currentSession || "";

  const sampleSheetData = [
    [classLabel, termLabel, sessionLabel, ""],
    [], 
    ["Student Name", "Admission No", "", ""]
  ];

  students.forEach(student =>
    sampleSheetData.push([
      `${student.lastname || ""} ${student.firstname || ""} ${student.middlename || ""}`,
      student.admission_no || "",
      "",
      ""
    ])
  );

  const worksheet = XLSX.utils.aoa_to_sheet(sampleSheetData);

  worksheet["!freeze"] = { rows: 3, cols: 0 };

  // Header style
  const headerStyle = {
    font: { bold: true, sz: 13 },
    alignment: { horizontal: "center" },
    border: {
      top: { style: "thin" },
      bottom: { style: "thin" },
      left: { style: "thin" },
      right: { style: "thin" }
    }
  };

  // Apply style to all 4 header cells in row 3
  ["A3", "B3", "C3", "D3"].forEach(cell => {
    if (worksheet[cell]) worksheet[cell].s = headerStyle;
  });

  // Column widths
  worksheet["!cols"] = [
    { wch: 30 }, // Student Name
    { wch: 18 }, // Admission No
    { wch: 10 }, // CAT1
    { wch: 10 }  // CAT2
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, `${classLabel}-students-list`);
  XLSX.writeFile(workbook, `${classLabel}-students-list.xlsx`);
};


//handling ties(as 1, 1, 3) not (1, 1, 2)
const getPositions1 = (scoreList) => {
  const sorted = [...scoreList].sort((a, b) => (b.score ?? -1) - (a.score ?? -1));
  const positions = {};
  let lastScore = null;
  let lastPosition = 0;
  let tieCount = 0;

  for (let index = 0; index < sorted.length; index++) {
    const item = sorted[index];
    if (item.score == null) {
      positions[item.student_id] = null;
      continue;
    }
    if (item.score === lastScore) {
      tieCount++;
      positions[item.student_id] = lastPosition;
    } else {
      lastPosition = index + 1;
      positions[item.student_id] = lastPosition;
      lastScore = item.score;
      tieCount = 0;
    }
  }
  return positions;
};

// same tie style you used (1,1,3)
const getPositions = (list) => {
  const sorted = [...list].sort((a, b) => (b.score ?? -1) - (a.score ?? -1));
  const pos = {};
  let lastScore = null;
  let lastPos = 0;

  for (let i = 0; i < sorted.length; i++) {
    const { student_id, score } = sorted[i];
    if (score == null) { pos[student_id] = null; continue; }
    if (score === lastScore) {
      pos[student_id] = lastPos;
    } else {
      lastPos = i + 1;          // competition ranking 1,1,3
      pos[student_id] = lastPos;
      lastScore = score;
    }
  }
  return pos;
};

// Precompute scores and positions
const processedStudents1 = useMemo(() => {
    const subjectList = subjectScores.map(s => ({ student_id: s.student_id, score: s.subject_score }));
    const subjectPositions = getPositions(subjectList);

    const termlyKey = `${currentTerm?.toLowerCase()}_term_score`;
    const termlyList = termlyScores.map(s => ({ student_id: s.student_id, score: s[termlyKey] }));
    const termlyPositions = getPositions(termlyList);

    const finalList = termlyScores.map(s => ({
      student_id: s.student_id,
      score: (s.first_term_score || 0) + (s.second_term_score || 0) + (s.third_term_score || 0)
    }));
    const finalPositions = getPositions(finalList);

    return students.map(student => ({
      ...student,
      subject_score: subjectScores.find(s => s.student_id === student.id)?.subject_score || null,
      subject_position: subjectPositions[student.id] || null,
      termly_score: termlyScores.find(s => s.student_id === student.id)?.[termlyKey] || null,
      termly_position: termlyPositions[student.id] || null,
      final_score: finalList.find(s => s.student_id === student.id)?.score || null,
      final_position: finalPositions[student.id] || null,
    }));
}, [students, subjectScores, termlyScores, currentTerm]);

const processedStudents = useMemo(() => {
  const termKey = `${(currentTerm || '').toLowerCase()}_term_score`;

  // --- class (this classroom only)
  const classTermList = termlyScores.map(s => ({
    student_id: s.student_id,
    score: s[termKey] ?? null,
  }));
  const classTermPositions = getPositions(classTermList);

  const classOverallList = termlyScores.map(s => ({
    student_id: s.student_id,
    score: (s.first_term_score || 0) + (s.second_term_score || 0) + (s.third_term_score || 0),
  }));
  const classOverallPositions = getPositions(classOverallList);

  // --- level (all classrooms in same level)
  const levelTermList = levelTermlyScores.map(s => ({
    student_id: s.student_id,
    score: s[termKey] ?? null,
  }));
  const levelTermPositions = getPositions(levelTermList);

  const levelOverallList = levelTermlyScores.map(s => ({
    student_id: s.student_id,
    score: (s.first_term_score || 0) + (s.second_term_score || 0) + (s.third_term_score || 0),
  }));
  const levelOverallPositions = getPositions(levelOverallList);

  // map for quick lookups
  const byId = Object.fromEntries(
    termlyScores.map(s => [s.student_id, s])
  );

  return students.map(stu => {
    const tRow = byId[stu.id];
    const termScore = tRow ? (tRow[termKey] ?? null) : null;
    const finalScore = tRow
      ? ( (tRow.first_term_score || 0) + (tRow.second_term_score || 0) + (tRow.third_term_score || 0) )
      : null;

    return {
      ...stu,
      // same numeric scores shown in both “class” and “level” columns
      termly_score: termScore,
      final_score: finalScore,

      // positions
      termly_position_class: classTermPositions[stu.id] ?? null,
      termly_position_level: levelTermPositions[stu.id] ?? null,
      final_position_class: classOverallPositions[stu.id] ?? null,
      final_position_level: levelOverallPositions[stu.id] ?? null,
    };
  });
}, [students, termlyScores, levelTermlyScores, currentTerm]);


  // Memoize filtered students to prevent new references
  const filteredStudents = useMemo(() => {
    return processedStudents.filter((student) => {
      if (selectedStatus !== 'all' && student.status !== selectedStatus) return false;
      const q = searchQuery.toLowerCase();
      return (
        student.firstname?.toLowerCase().includes(q) ||
        student.lastname?.toLowerCase().includes(q) ||
        student.middlename?.toLowerCase().includes(q) ||
        student.admission_no?.toLowerCase().includes(q) ||
        student.student_email?.toLowerCase().includes(q)
      );
    });
  }, [processedStudents, selectedStatus, searchQuery]);

  // Sort options
  const sortOptions = [
    { value: 'a-z', label: 'A-Z' },
    { value: 'z-a', label: 'Z-A' },
    { value: 'subject_position-asc', label: 'Subject Position (Asc)' },
    { value: 'subject_position-desc', label: 'Subject Position (Desc)' },
    { value: 'termly_position-asc', label: 'Termly Position (Asc)' },
    { value: 'termly_position-desc', label: 'Termly Position (Desc)' },
    { value: 'final_position-asc', label: 'Final Position (Asc)' },
    { value: 'final_position-desc', label: 'Final Position (Desc)' },
  ];

  // Compute sorted students with useMemo instead of useEffect
  const sortedStudents = useMemo(() => {
    let temp = [...filteredStudents];
    switch (sortOption) {
      case 'a-z':
        temp.sort((a, b) => `${a.lastname} ${a.firstname}`.localeCompare(`${b.lastname} ${b.firstname}`));
        break;
      case 'z-a':
        temp.sort((a, b) => `${b.lastname} ${b.firstname}`.localeCompare(`${a.lastname} ${a.firstname}`));
        break;
      case 'subject_position-asc':
        temp.sort((a, b) => (a.subject_position || Infinity) - (b.subject_position || Infinity));
        break;
      case 'subject_position-desc':
        temp.sort((a, b) => (b.subject_position || -Infinity) - (a.subject_position || -Infinity));
        break;
      case 'termly_position-asc':
        temp.sort((a, b) => (a.termly_position || Infinity) - (b.termly_position || Infinity));
        break;
      case 'termly_position-desc':
        temp.sort((a, b) => (b.termly_position || -Infinity) - (a.termly_position || -Infinity));
        break;
      case 'final_position-asc':
        temp.sort((a, b) => (a.final_position || Infinity) - (b.final_position || Infinity));
        break;
      case 'final_position-desc':
        temp.sort((a, b) => (b.final_position || -Infinity) - (a.final_position || -Infinity));
        break;
      default:
        break;
    }
    return temp;
  }, [filteredStudents, sortOption]);

  // Pagination
  const totalItems = filteredStudents.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedStudents = useMemo(() => {
    return sortedStudents.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );
  }, [sortedStudents, currentPage, itemsPerPage]);

  // Handle page change
  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  // Reset to page 1 when filter/search/limit/sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedStatus, searchQuery, itemsPerPage, sortOption]);

  // Handle select all checkbox
  const handleSelectAll = () => {
    if (selectedStudents.length === filteredStudents.length) {
      setSelectedStudents([]);
      setSelectAll(false);
    } else {
      setSelectedStudents(filteredStudents.map(student => student.id));
      setSelectAll(true);
    }
  };

  // Handle individual student checkbox
  const handleStudentSelect = (studentId) => {
    setSelectedStudents(prev =>
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  // Update select all when individual selections change
  useEffect(() => {
    const allSelected = paginatedStudents.length > 0 && 
                       paginatedStudents.every(student => selectedStudents.includes(student.id));
    setSelectAll(allSelected);
  }, [selectedStudents, paginatedStudents]);

  // Dropdown actions for each row
  const handleActionClick = (id) => {
    setActionDropdownId(actionDropdownId === id ? null : id);
  };
  
  const closeDropdown = () => setActionDropdownId(null);

  // For closing dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (!e.target.closest('.action-dropdown')) setActionDropdownId(null);
    };
    if (actionDropdownId !== null) {
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [actionDropdownId]);

  // Handle student actions
  const handleViewDetails = (student) => {
    setSelectedStudent(student);
    setShowDetailsModal(true);
    closeDropdown();
  };

  const handleEnterScores = (studentUuid) => {
    closeDropdown();
    navigate(`/enter-scores/${studentUuid}/${classId}`);
  };

  const handleViewResult = (studentId) => {
    closeDropdown();
    navigate(`/student-single-result?studentUuid=${studentId}&className=${currentClass?.class_name}&session=${currentSession}&term=${currentTerm}`);
    // navigate(`/student-single-result?studentUuid=${userInfo?.uuid}&className=${selectedResult?.class_name}&session=${selectedResult?.session}&term=${selectedTerm || userInfo.currentTerm}`);
  };

  const handleEditStudent = (studentId) => {
    closeDropdown();
    navigate(`/edit-student/${studentId}`);
  };

  // const handleEnterClassResult = () => {
  //   navigate(`/results?classOption=${currentClass?.class_name}`);
  // };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Calculate age
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return 'N/A';
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Bulk action options
  const bulkActionOptions = [
    // { value: 'confirm', label: 'Confirm Student(s)', icon: UserCheck },
    { value: 'transfer', label: 'Class Transfer', icon: UserPlus },
    // { value: 'suspend', label: 'Suspend Student(s)', icon: UserX },
    // { value: 'expel', label: 'Expel Student(s)', icon: Ban },
    // { value: 'modify_offered_subjects', label: 'Modify Offered Subjects', icon: BookA },
    { value: 'psychomotor', label: 'Enter Psychomotor Skills', icon: Brain },
    { value: 'principal_comment', label: 'Enter Principal Comments', icon: MessageCircle },
    { value: 'teacher_comment', label: 'Enter Form Teacher Comments', icon: MessageSquareDot },
    // { value: 'assign_school_post', label: 'Assign School Post', icon: GraduationCap },
    // { value: 'assign_class_post', label: 'Assign Class Post', icon: AwardIcon },
    { value: 'update_profile', label: 'Update Student Basic Profile', icon: UserCheck },
    { value: 'remove_permanently', label: 'Remove Permanently', icon: Trash },
    { value: 'download_results_pdf', label: 'Download Results (PDF)', icon: FileText },
  ];

  // Class options for transfer //
  const classOptions = allClasses.map((c) => ({
    value: c.class_name,
    label: c.class_name,
  }));

  // Handle modal actions
  const openModal = (action, studentIds = selectedStudents, student = null) => {
    if (action === 'download_results_pdf') {
      handleOpenBatchResultsModal(studentIds);
      return;
    }
    let initialData = {};
    if (studentIds.length === 1 && student) {
      switch (action) {
        case 'update_profile':
          initialData = {
            lastname: student.lastname || '',
            firstname: student.firstname || '',
            middlename: student.middlename || '',
            phone: student.student_phone || '',
            currentProfilePicture: student.profile_picture || null,
          };
          break;
        case 'modify_offered_subjects':
          initialData = {
            subjectIds: student.subjects?.map(s => s.id) || [],
            selectAllSubjects: student.subjects?.length === subjects.length
          };
          break;
        case 'psychomotor':
          const psychoKeys = Object.keys(psychomotorSkills || {}).map(skill => skill.toLowerCase().replace(/ /g, '_'));
          initialData = psychoKeys.reduce((acc, key) => {
            acc[key] = student.psychomotors?.[key] || '';
            return acc;
          }, {});
          break;
        case 'principal_comment':
          initialData = { comment: student.principal_comments || '' };
          break;
        case 'teacher_comment':
          initialData = { comment: student.form_teacher_comments || '' };
          break;
        case 'assign_school_post':
          initialData = { post: student.post || '' };
          break;
        case 'assign_class_post':
          initialData = { post: student.class_post || '' };
          break;
      }
    } else if (action === 'modify_offered_subjects') {
      initialData = {
        subjectIds: [],
        selectAllSubjects: false
      };
    }
    setSelectedAction(action);
    setModalData({ studentIds, action, initialData });
    setShowActionModal(true);
    setActionDropdownId(null);
  };

  const closeModal = () => {
    setShowActionModal(false);
    setModalData({});
    setSelectedAction(null);
  };

  // Handle form submission
  const handleSubmit = async (formData) => {
    // console.log({formData});
    
    try {
      const payload = new FormData();
      payload.append('studentIds', JSON.stringify(modalData.studentIds));
      payload.append('action', modalData.action);
      payload.append('fromClass', currentClass?.class_name || '');

      for (const [key, value] of Object.entries(formData)) {
        if (key === 'profile_picture' && value instanceof File) {
          payload.append('profile_picture', value);
        } else if (key === 'subjectIds' && Array.isArray(value)) {
          payload.append('subjectIds', JSON.stringify(value));
        } else if (key !== 'currentProfilePicture' && key !== 'selectAllSubjects' && value !== undefined && value !== null) {
          payload.append(key, value);
        }
      }

      const response = await fetch(`${API_BASE_URL}/api/web/admin/all-students-by-class-query-actions`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${userInfo?.token || localStorage.getItem('authToken')}`,
        },
        body: payload,
      });

      const result = await response.json();
      if (result.success) {
        alert('Action completed successfully!');
        setSelectedStudents([]);
        closeModal();
        refetchStudents();
      } else {
        alert(`Failed: ${result.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error performing action');
    }
  };

  // Action Modal Component
  const ActionModal = ({ action, studentIds, onSubmit, onClose, psychomotorSkills, classOptions, currentClass, initialData }) => {
    const [formData, setFormData] = useState(initialData || {});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [preview, setPreview] = useState(initialData?.currentProfilePicture || null);
    const [isGeneratingAI, setIsGeneratingAI] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [commentType, setCommentType] = useState('termly'); // 'termly' or 'cumulative'

    useEffect(() => {
      setFormData(initialData || {});
      setPreview(initialData?.currentProfilePicture || null);
      setError('');
    }, [initialData]);

    const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (file) {
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
        if (!allowedTypes.includes(file.type)) {
          setError('Please upload a JPEG, PNG, or GIF image.');
          setFormData({ ...formData, profile_picture: null });
          setPreview(initialData?.currentProfilePicture || null);
          return;
        }
        if (file.size > 5 * 1024 * 1024) {
          setError('File size exceeds 5MB limit.');
          setFormData({ ...formData, profile_picture: null });
          setPreview(initialData?.currentProfilePicture || null);
          return;
        }
        setFormData({ ...formData, profile_picture: file });
        setPreview(URL.createObjectURL(file));
        setError('');
      } else {
        setFormData({ ...formData, profile_picture: null });
        setPreview(initialData?.currentProfilePicture || null);
        setError('');
      }
    };

    const handleModalSubmit = async (e) => {
      e.preventDefault();
      setLoading(true);
      setError('');
      try {
        if (action === 'delete' && !window.confirm('Are you sure you want to delete these students?')) {
          setLoading(false);
          return;
        }
        if (action === 'update_profile') {
          if (!formData.firstname || !formData.lastname || !formData.phone) {
            setError('First name, last name, and phone are required.');
            setLoading(false);
            return;
          }
        }
        if (action === 'modify_offered_subjects' && (!formData.subjectIds || formData.subjectIds.length === 0)) {
          setError('Please select at least one subject.');
          setLoading(false);
          return;
        }
        await onSubmit(formData);
      } catch (error) {
        console.error('ActionModal Error:', error);
        setError('Failed to complete action. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    const handleSelectAllSubjects = () => {
      if (formData.selectAllSubjects) {
        setFormData({ ...formData, subjectIds: [], selectAllSubjects: false });
      } else {
        setFormData({ 
          ...formData, 
          subjectIds: subjects.map(subject => subject.id),
          selectAllSubjects: true 
        });
      }
    };

    const handleSubjectSelect = (subjectId) => {
      setFormData(prev => {
        const newSubjectIds = prev.subjectIds.includes(subjectId)
          ? prev.subjectIds.filter(id => id !== subjectId)
          : [...prev.subjectIds, subjectId];
        return { 
          ...prev, 
          subjectIds: newSubjectIds,
          selectAllSubjects: newSubjectIds.length === subjects.length
        };
      });
    };

    // Helper function to calculate grade from score
    const calculateGrade = (score) => {
      if (!score || score === null) return null;
      const numScore = parseFloat(score);
      if (numScore >= 70) return 'A';
      if (numScore >= 60) return 'B';
      if (numScore >= 50) return 'C';
      if (numScore >= 40) return 'D';
      if (numScore >= 30) return 'E';
      return 'F';
    };

    // Generate comment based on performance (in-app) - concise, two sentences, no averages
    const generateComment = () => {
      if (studentIds.length !== 1) {
        alert('Comment generation is only available for a single student.');
        return;
      }

      setIsGenerating(true);
      try {
        const student = students.find(s => s.id === studentIds[0]);
        if (!student) {
          alert('Student not found.');
          setIsGenerating(false);
          return;
        }

        const useTermly = commentType === 'termly';
        const termScore = useTermly ? (student.termly_score || null) : (student.final_score || null);
        const termPosition = useTermly ? (student.termly_position_class || null) : (student.final_position_class || null);
        const psychomotor = student.psychomotors || {};
        const subjectScores = student.subject_scores || [];

        // Subject strengths/weaknesses (no average text)
        const strengths = [];
        const weaknesses = [];
        if (subjectScores.length > 0) {
          subjectScores.forEach(subj => {
            const score = parseFloat(subj.total_score) || 0;
            const grade = subj.grade || calculateGrade(score);
            if (grade === 'A' || grade === 'B' || score >= 70) {
              strengths.push(subj.subject_name);
            } else if (grade === 'F' || grade === 'E' || score < 40) {
              weaknesses.push(subj.subject_name);
            }
          });
        }

        // Psychomotor highlights
        const psychomotorStrengths = [];
        const psychomotorWeaknesses = [];
        if (psychomotor && typeof psychomotor === 'object') {
          Object.entries(psychomotor).forEach(([skill, score]) => {
            const numScore = parseFloat(score);
            if (numScore >= 4) psychomotorStrengths.push(skill.replace(/_/g, ' '));
            else if (numScore <= 2 && numScore > 0) psychomotorWeaknesses.push(skill.replace(/_/g, ' '));
          });
        }

        // Performance label without showing numeric average
        let performanceLabel = 'steady performance';
        const overallScore = parseFloat(termScore) || 0;
        if (overallScore >= 70) performanceLabel = 'strong performance';
        else if (overallScore >= 60) performanceLabel = 'good performance';
        else if (overallScore >= 50) performanceLabel = 'fair performance';
        else if (overallScore > 0) performanceLabel = 'performance needing improvement';

        // Sentence 1: position + overall note (no average number)
        const period = useTermly ? 'this term' : 'cumulatively';
        const firstSentence = `${student.firstname} showed ${performanceLabel} ${period}.`;

        // Sentence 2: concise focus areas
        const focusBits = [];
        if (strengths.length > 0) focusBits.push(`strong in ${strengths.slice(0, 3).join(', ')}`);
        if (weaknesses.length > 0) focusBits.push(`needs focus in ${weaknesses.slice(0, 3).join(', ')}`);
        if (psychomotorStrengths.length > 0) focusBits.push(`good ${psychomotorStrengths.slice(0, 2).join(', ')}`);
        if (psychomotorWeaknesses.length > 0) focusBits.push(`work on ${psychomotorWeaknesses.slice(0, 2).join(', ')}`);
        const secondSentence = focusBits.length > 0
          ? `${focusBits.join('; ')}. Keep pushing forward.`
          : 'Keep pushing forward.';

        setFormData(prev => ({
          ...prev,
          comment: `${firstSentence} ${secondSentence}`.trim()
        }));
      } catch (error) {
        console.error('Error generating comment:', error);
        alert('Failed to generate comment. Please try again.');
      } finally {
        setIsGenerating(false);
      }
    };

    // Generate AI comment
    const generateAIComment = async () => {
      if (studentIds.length !== 1) {
        alert('AI comment generation is only available for a single student.');
        return;
      }

      setIsGeneratingAI(true);
      try {
        const student = students.find(s => s.id === studentIds[0]);
        if (!student) {
          alert('Student not found.');
          setIsGeneratingAI(false);
          return;
        }

        // Get student's results based on comment type
        const useTermly = commentType === 'termly';
        const termScore = useTermly ? (student.termly_score || null) : (student.final_score || null);
        const termPosition = useTermly ? (student.termly_position_class || null) : (student.final_position_class || null);
        const psychomotor = student.psychomotors || {};
        const subjectScores = student.subject_scores || [];

        // Calculate average and get subject info
        let averageScore = 0;
        let topSubjects = [];
        let weakSubjects = [];
        
        if (subjectScores.length > 0) {
          const totalScore = subjectScores.reduce((sum, subj) => sum + (parseFloat(subj.total_score) || 0), 0);
          averageScore = subjectScores.length > 0 ? totalScore / subjectScores.length : 0;
          
          subjectScores.forEach(subj => {
            const score = parseFloat(subj.total_score) || 0;
            const grade = subj.grade || calculateGrade(score);
            if (grade === 'A' || grade === 'B' || score >= 70) {
              topSubjects.push(subj.subject_name);
            } else if (grade === 'F' || grade === 'E' || score < 40) {
              weakSubjects.push(subj.subject_name);
            }
          });
        } else if (termScore) {
          averageScore = parseFloat(termScore);
        }

        const strongSkills = Object.entries(psychomotor || {}).filter(([_, score]) => parseFloat(score) >= 4).map(([skill]) => skill.replace(/_/g, ' '));
        const areasToImprove = Object.entries(psychomotor || {}).filter(([_, score]) => parseFloat(score) <= 2 && parseFloat(score) > 0).map(([skill]) => skill.replace(/_/g, ' '));

        // Determine comment type
        const commentTypeText = action === 'principal_comment' ? "principal's" : "form teacher's";
        const period = useTermly ? 'this term' : 'cumulatively';
        
        // Prepare the prompt for the AI - Using OpenAI API (free tier)
        const prompt = `Write a ${commentTypeText} comment in exactly two sentences, not exceeding 30 words total. First sentence should mention position and academic performance. Second sentence should focus on psychomotor skills and encouragement.

Student Performance:
- Name: ${student.firstname}
- Position (${period}): ${termPosition ? `${termPosition}${termPosition === 1 ? 'st' : termPosition === 2 ? 'nd' : termPosition === 3 ? 'rd' : 'th'}` : 'N/A'}
- Average: ${averageScore > 0 ? averageScore.toFixed(1) : 'N/A'}
- Top Subjects: ${topSubjects.join(', ') || 'N/A'}
- Weak Subjects: ${weakSubjects.join(', ') || 'N/A'}
- Strong Skills: ${strongSkills.join(', ') || 'N/A'}
- Areas to Improve: ${areasToImprove.join(', ') || 'N/A'}

Format: [Position and academic performance]. [Psychomotor skills and encouragement].`;

        // Using OpenAI API (free tier with credits)
        let generatedText = '';
        
        try {
          // Try OpenAI API - using GPT-3.5-turbo (available in free tier)
          // To use OpenAI API:
          // 1. Get your API key from https://platform.openai.com/api-keys
          // 2. Add it to your .env file as: REACT_APP_OPENAI_API_KEY=your_key_here
          // 3. Restart your development server
          // Note: OpenAI free tier provides $5 in credits for new accounts
          
          const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
          if (!apiKey) {
            throw new Error('OpenAI API key not found. Please add REACT_APP_OPENAI_API_KEY to your .env file.');
          }
          
          const openAIResponse = await fetch('https://api.openai.com/v1/chat/completions', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${apiKey}`
            },
            body: JSON.stringify({
              model: 'gpt-3.5-turbo', // Free tier compatible model
              messages: [
                {
                  role: 'system',
                  content: `You are a school principal/form teacher writing a comment for a student's report card. Write exactly two sentences, not exceeding 30 words total. First sentence should mention position and academic performance. Second sentence should focus on psychomotor skills and encouragement. Be concise and professional.`
                },
                {
                  role: 'user',
                  content: prompt
                }
              ],
              max_tokens: 100,
              temperature: 0.7
            }),
          });

          if (!openAIResponse.ok) {
            const errorData = await openAIResponse.json().catch(() => ({}));
            throw new Error(errorData.error?.message || `OpenAI API error: ${openAIResponse.status} ${openAIResponse.statusText}`);
          }
          
          const openAIData = await openAIResponse.json();
          
          if (openAIData.error) {
            // If API key is missing or invalid, throw to use fallback
            throw new Error(openAIData.error.message || 'OpenAI API error');
          }
          
          if (openAIData.choices && openAIData.choices[0]?.message?.content) {
            generatedText = openAIData.choices[0].message.content
              .trim()
              .replace(/^["']|["']$/g, '') // Remove quotes if present
              .replace(/\s+/g, ' ') // Normalize spaces
              .substring(0, 200); // Limit length
            
            setFormData(prev => ({
              ...prev,
              comment: generatedText
            }));
            setIsGeneratingAI(false);
            return; // Success, exit early
          } else {
            throw new Error('Unexpected response format from OpenAI');
          }
        } catch (apiError) {
          console.log('OpenAI API error, using fallback:', apiError);
          
          // Show user-friendly message if API key is missing
          if (apiError.message && apiError.message.includes('API key not found')) {
            alert('OpenAI API key not configured. Using template-based comment generation instead.\n\nTo enable AI comments:\n1. Get your API key from https://platform.openai.com/api-keys\n2. Add REACT_APP_OPENAI_API_KEY=your_key_here to your .env file\n3. Restart your development server');
          }
          
          // Fallback: Generate comment using intelligent template
          if (averageScore > 0 && termPosition) {
            const positionText = termPosition === 1 ? '1st' : termPosition === 2 ? '2nd' : termPosition === 3 ? '3rd' : `${termPosition}th`;
            const performanceText = averageScore >= 70 ? 'excellent' : averageScore >= 60 ? 'good' : averageScore >= 50 ? 'fair' : 'needs improvement';
            
            let firstSentence = `${student.firstname} secured ${positionText} position ${period} with ${performanceText} academic performance (average: ${averageScore.toFixed(1)}).`;
            if (topSubjects.length > 0) {
              firstSentence += ` Strong in ${topSubjects.slice(0, 2).join(' and ')}.`;
            }
            
            let secondSentence = '';
            if (strongSkills.length > 0) {
              secondSentence = `Demonstrates excellent ${strongSkills[0]} and ${strongSkills.length > 1 ? strongSkills[1] : 'skills'}.`;
            } else if (areasToImprove.length > 0) {
              secondSentence = `Should focus on improving ${areasToImprove[0]}.`;
            } else {
              secondSentence = 'Keep up the good work!';
            }
            
            generatedText = `${firstSentence} ${secondSentence}`;
          } else {
            // If no scores available, generate a generic encouraging comment
            generatedText = `${student.firstname} has shown ${strongSkills.length > 0 ? `good ${strongSkills[0]}` : 'consistent effort'} this term. ${strongSkills.length > 0 ? 'Keep up the excellent work!' : 'Continue to strive for improvement.'}`;
          }
          
          setFormData(prev => ({
            ...prev,
            comment: generatedText.trim()
          }));
        }
      } catch (error) {
        console.error('Error generating AI comment:', error);
        // Error already handled in the catch block above with fallback
        // Only show alert if it's not an API key issue (already alerted)
        if (!error.message || !error.message.includes('API key not found')) {
          alert('Failed to generate AI comment. Using template-based generation instead.');
        }
      } finally {
        setIsGeneratingAI(false);
      }
    };

    const transferReasonOptions = [
      { value: 'promotion', label: 'Promotion' },
      { value: 'demotion', label: 'Demotion' },
      { value: 'switch', label: 'Switch' },
    ];

    const renderActionForm = () => {
      switch (action) {
        case 'confirm':
          return (
            <div className="text-center">
              <p className="mb-4">Are you sure you want to confirm {studentIds.length} student(s)?</p>
            </div>
          );
        case 'transfer':
          return (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-400">From Class</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border rounded bg-gray-100 dark:bg-gray-700"
                  value={currentClass || ''}
                  readOnly
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-400">To Class</label>
                <Select
                  options={classOptions.filter((option) => option.value !== currentClass)}
                  value={classOptions.find((option) => option.value === formData.toClass) || null}
                  onChange={(selected) => setFormData({ ...formData, toClass: selected?.value || '' })}
                  placeholder="Select destination class"
                  isClearable
                  required
                  className="text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-400">Transfer Reason</label>
                <Select
                  options={transferReasonOptions}
                  value={transferReasonOptions.find((option) => option.value === formData.reason) || null}
                  onChange={(selected) => setFormData({ ...formData, reason: selected?.value || '' })}
                  placeholder="Select transfer reason"
                  isClearable
                  required
                  className="text-gray-900 dark:text-white"
                />
              </div>
            </div>
          );
        case 'suspend':
          return (
            <div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-400">Suspension Duration</label>
                <select
                  className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  value={formData.duration || ''}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  required
                >
                  <option value="">Select duration</option>
                  <option value="1_week">1 Week</option>
                  <option value="2_weeks">2 Weeks</option>
                  <option value="1_month">1 Month</option>
                  <option value="1_term">1 Term</option>
                  <option value="indefinite">Indefinite</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-400">Reason for Suspension (optional)</label>
                <textarea
                  className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows="3"
                  placeholder="State the reason for suspension..."
                  value={formData.reason || ''}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                />
              </div>
            </div>
          );
        case 'expel':
          return (
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-400">Reason for Expel</label>
              <textarea
                className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                rows="3"
                placeholder="State the reason for expelling..."
                value={formData.reason || ''}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                required
              />
            </div>
          );
        case 'modify_offered_subjects':
          return (
            <div className="space-y-4">
              <label className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-400">Check or Uncheck Subjects</label>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.selectAllSubjects || false}
                  onChange={handleSelectAllSubjects}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <label className="ml-2 text-sm text-gray-600 dark:text-gray-400">Select All Subjects</label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                {subjects.map((subject) => (
                  <div key={subject.id} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.subjectIds.includes(subject.id)}
                      onChange={() => handleSubjectSelect(subject.id)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                    <label className="ml-2 text-sm text-gray-900 dark:text-white">{subject.subject_name}</label>
                  </div>
                ))}
              </div>
              {error && <p className="text-red-600 text-sm">{error}</p>}
            </div>
          );
        case 'psychomotor':
          return (
            <div className="space-y-4">
              <h4 className="font-medium text-gray-900 dark:text-white">Psychomotor Skills Assessment</h4>
              {psychomotorSkills &&
                Object.entries(psychomotorSkills).map(([key, skill]) => (
                  <div key={key} className="flex items-center gap-4">
                    <label className="w-24 text-sm text-gray-600 dark:text-gray-400">{skill}:</label>
                    <select
                      className="flex-1 px-2 py-1 border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      value={formData[skill.toLowerCase().replace(/ /g, '_')] || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, [skill.toLowerCase().replace(/ /g, '_')]: e.target.value })
                      }
                    >
                      <option value="">Select</option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                      <option value="5">5</option>
                    </select>
                  </div>
                ))}
            </div>
          );
        case 'principal_comment':
          return (
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-400">Principal's Comment</label>
              <div className="mb-3">
                <label className="block text-xs font-medium mb-1 text-gray-500 dark:text-gray-400">Comment Type</label>
                <select
                  value={commentType}
                  onChange={(e) => setCommentType(e.target.value)}
                  className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                  <option value="termly">Termly (Current Term)</option>
                  <option value="cumulative">Cumulative (All Terms)</option>
                </select>
              </div>
              <div className="flex gap-2 mb-2">
                <button
                  type="button"
                  onClick={generateComment}
                  disabled={isGenerating || isGeneratingAI || studentIds.length !== 1}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800 text-blue-700 dark:text-blue-200 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Generate Comment
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={generateAIComment}
                  disabled={isGenerating || isGeneratingAI || studentIds.length !== 1}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-purple-100 hover:bg-purple-200 dark:bg-purple-900 dark:hover:bg-purple-800 text-purple-700 dark:text-purple-200 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isGeneratingAI ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generating AI...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4" />
                      Generate AI Comment
                    </>
                  )}
                </button>
              </div>
              <textarea
                className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                rows="4"
                placeholder="Enter principal's comment..."
                value={formData.comment || ''}
                onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                required
              />
              {studentIds.length !== 1 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Note: Comment generation is only available for a single student.
                </p>
              )}
            </div>
          );
        case 'teacher_comment':
          return (
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-400">Form Teacher's Comment</label>
              <div className="mb-3">
                <label className="block text-xs font-medium mb-1 text-gray-500 dark:text-gray-400">Comment Type</label>
                <select
                  value={commentType}
                  onChange={(e) => setCommentType(e.target.value)}
                  className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                  <option value="termly">Termly (Current Term)</option>
                  <option value="cumulative">Cumulative (All Terms)</option>
                </select>
              </div>
              <div className="flex gap-2 mb-2">
                <button
                  type="button"
                  onClick={generateComment}
                  disabled={isGenerating || isGeneratingAI || studentIds.length !== 1}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800 text-blue-700 dark:text-blue-200 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Generate Comment
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={generateAIComment}
                  disabled={isGenerating || isGeneratingAI || studentIds.length !== 1}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-purple-100 hover:bg-purple-200 dark:bg-purple-900 dark:hover:bg-purple-800 text-purple-700 dark:text-purple-200 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isGeneratingAI ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generating AI...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4" />
                      Generate AI Comment
                    </>
                  )}
                </button>
              </div>
              <textarea
                className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                rows="4"
                placeholder="Enter form teacher's comment..."
                value={formData.comment || ''}
                onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                required
              />
              {studentIds.length !== 1 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Note: Comment generation is only available for a single student.
                </p>
              )}
            </div>
          );
        case 'assign_school_post':
          return (
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-400">School Post</label>
              <select
                className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                value={formData.post || ''}
                onChange={(e) => setFormData({ ...formData, post: e.target.value })}
                required
              >
                <option value="">Select post</option>
                <option value="head_prefect">Head Prefect</option>
                <option value="deputy_prefect">Deputy Prefect</option>
                <option value="library_prefect">Library Prefect</option>
                <option value="sports_prefect">Sports Prefect</option>
              </select>
            </div>
          );
        case 'assign_class_post':
          return (
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-400">Class Post</label>
              <select
                className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                value={formData.post || ''}
                onChange={(e) => setFormData({ ...formData, post: e.target.value })}
                required
              >
                <option value="">Select post</option>
                <option value="class_captain">Class Captain</option>
                <option value="assistant_class_captain">Assistant Class Captain</option>
                <option value="class_secretary">Class Secretary</option>
              </select>
            </div>
          );
        case 'update_profile':
          return (
            <div className="space-y-4">
              {error && <p className="text-red-600 text-sm">{error}</p>}
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-400">Last Name</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Last name"
                  value={formData.lastname || ''}
                  onChange={(e) => setFormData({ ...formData, lastname: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-400">First Name</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="First name"
                  value={formData.firstname || ''}
                  onChange={(e) => setFormData({ ...formData, firstname: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-400">Middle Name</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Middle name"
                  value={formData.middlename || ''}
                  onChange={(e) => setFormData({ ...formData, middlename: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-400">Phone Number</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Phone number"
                  value={formData.phone || ''}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-400">Profile Picture</label>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif"
                  className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  onChange={handleFileChange}
                />
                {preview && (
                  <div className="mt-2">
                    <img
                      src={preview}
                      alt="Profile preview"
                      className="w-24 h-24 object-cover rounded"
                    />
                    {formData.profile_picture && (
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({ ...formData, profile_picture: null });
                          setPreview(initialData?.currentProfilePicture || null);
                          setError('');
                        }}
                        className="mt-1 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        Remove New Image
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        case 'remove_permanently':
          return (
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-600 dark:text-gray-400">Reason for Removing Permanently</label>
              <textarea
                className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                rows="3"
                placeholder="State the reason for removing permanently..."
                value={formData.reason || 'No reason'}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                required
              />
            </div>
          );
        default:
          return <div>Unknown action</div>;
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md max-h-[80vh] overflow-y-auto">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {action.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())} - {studentIds.length} Student(s)
          </h3>
          <form onSubmit={handleModalSubmit}>
            {renderActionForm()}
            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Processing...' : 'Submit'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Format position (1st, 2nd, 3rd, etc.)
  const showPositionInActualFormat = (position) => {
    if (position === 1) return '1st';
    if (position === 2) return '2nd';
    if (position === 3) return '3rd';
    return `${position}th`;
  };

  // Get score color based on value
  const getScoreColor = (score) => {
    if (score == null) return 'bg-gray-100 text-gray-800';
    if (score >= 70) return 'bg-green-100 text-green-800';
    if (score >= 60) return 'bg-blue-100 text-blue-800';
    if (score >= 40) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const openSubjectModal = (className) => {
    setSelectedClass(className);
    setIsSubjectModalOpen(true);
  };

  const closeSubjectModal = () => {
    setIsSubjectModalOpen(false);
    setSelectedClass(null);
    setSelectedSubject(null);
  };

  const handleSubjectSubmit = () => {
    if (!selectedSubject) {
      alert('Please select a subject.');
      return;
    }
    navigate(`/results?classOption=${encodeURIComponent(selectedClass)}&subjectOption=${encodeURIComponent(selectedSubject.value)}`);
    closeSubjectModal();
  };

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: '42px',
      borderColor: '#d1d5db',
      backgroundColor: '#fff',
      '&:hover': { borderColor: '#3b82f6' },
      '[data-theme="dark"] &': {
        backgroundColor: '#1f2937',
        borderColor: '#4b5563',
        color: '#fff',
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#374151',
      '[data-theme="dark"] &': {
        color: '#fff',
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#3b82f6' : '#fff',
      color: state.isSelected ? '#fff' : '#374151',
      '&:hover': {
        backgroundColor: state.isSelected ? '#3b82f6' : '#f3f4f6',
      },
      '[data-theme="dark"] &': {
        backgroundColor: state.isSelected ? '#3b82f6' : '#1f2937',
        color: state.isSelected ? '#fff' : '#d1d5db',
        '&:hover': {
          backgroundColor: state.isSelected ? '#3b82f6' : '#374151',
        },
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: '#fff',
      '[data-theme="dark"] &': {
        backgroundColor: '#1f2937',
      },
    }),
    input: (provided) => ({
      ...provided,
      color: '#374151',
      '[data-theme="dark"] &': {
        color: '#fff',
      },
    }),
  };

  return (
    <div className="space-y-6">
      {/* Class Information Header */}
      {selectedStudents.length < 1 && (
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-6 text-white">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div className="space-y-2">
              <h1 className="text-2xl font-bold">{currentClass.class_name || 'Loading...'}</h1>
              <div className="flex flex-wrap items-center gap-4 text-blue-100">
                <div className="flex items-center space-x-2">
                  <Users className="w-5 h-5" />
                  <span>{students.length} Students • </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar className="w-5 h-5" />
                  <span>{currentSession || 'N/A'} Session • </span>
                </div>
                <div className="flex items-center space-x-2">
                  <GraduationCap className="w-5 h-5" />
                  <span>{currentTerm || 'N/A'} Term</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4">
                <button
                  // onClick={handleEnterClassResult}
                  onClick={() => {downloadSample()}}
                  className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-lg transition-colors"
                >
                  <FileText className="w-5 h-5" />
                  <span>Export Students</span>
                </button>

                <button
                  // onClick={handleEnterClassResult}
                  onClick={() => openSubjectModal(currentClass?.class_name)}
                  className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-lg transition-colors"
                >
                  <FileText className="w-5 h-5" />
                  <span>Enter Class Result</span>
                </button>
            </div>
            
          </div>
        </div>
      )}

      {/* Bulk Actions */}
      {selectedStudents.length > 0 && (
        <div className="bg-white dark:bg-gray-800 p-4 mb-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="font-medium mb-3 text-gray-900 dark:text-white">
            Bulk Actions ({selectedStudents.length} student{selectedStudents.length > 1 ? 's' : ''} selected)
          </h3>
          <div className="flex flex-wrap gap-2">
            {bulkActionOptions
              .filter((action) => !(selectedStudents.length > 1 && action.value === 'update_profile'))
              .map((action) => (
                <button
                  key={action.value}
                  onClick={() => openModal(action.value)}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-sm text-gray-900 dark:text-white"
                >
                  <action.icon className="w-4 h-4" />
                  {action.label}
                </button>
              ))}
          </div>
        </div>
      )}

      {/* Students Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Students</h2>
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative">
                <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search students..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
              <select
                value={selectedStatus}
                onChange={e => setSelectedStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Students</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <Select
                value={sortOptions.find(option => option.value === sortOption)}
                onChange={(selected) => setSortOption(selected?.value || 'a-z')}
                options={sortOptions}
                className="w-48 text-gray-900 dark:text-white"
                placeholder="Sort by"
              />
              <select
                value={itemsPerPage}
                onChange={e => setItemsPerPage(Number(e.target.value))}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {[10, 20, 30, 50, 100].map(limit => (
                  <option key={limit} value={limit}>{limit} per page</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Student</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Admission No.</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {/* <span title="Subject score this term">Subject Score | Position</span> */}
                    <span title={`Score for this term, in ${currentClass?.class_name}`}>This Term Score In Class | Position</span>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {/* <span title="Subject score this term">Subject Score | Position</span> */}
                    <span title={`Score for this term, in entire classlevel(eg all of JSS1)`}>This Term Score In Entire ClassLevel | Position</span>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {/* <span title="Score for this term only">Termly Score | Position</span> */}
                    <span title={`Score for all terms so far, in ${currentClass?.class_name}`}>All Terms Score In Class | Position</span>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {/* <span title="Sum of the three term scores">Final Score | Position</span> */}
                    <span title={`Score for all terms, in entire classlevel, e.g(All terms in entire JSS1)`}>All Terms Score In Entire ClassLevel | Position</span>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {studentsLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-6 py-4">
                      <div className="w-4 h-4 bg-gray-300 dark:bg-gray-600 rounded"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                        <div className="space-y-2">
                          <div className="h-4 w-32 bg-gray-300 dark:bg-gray-600 rounded"></div>
                          <div className="h-3 w-24 bg-gray-300 dark:bg-gray-600 rounded"></div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-4 w-20 bg-gray-300 dark:bg-gray-600 rounded"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-6 w-16 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-6 w-16 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-6 w-16 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-6 w-16 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="h-6 w-16 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                    </td>
                  </tr>
                ))
              ) : paginatedStudents.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                    {studentsError ? 'Error loading students' : 'No students found.'}
                  </td>
                </tr>
              ) : (
                paginatedStudents.map((student, i) => (
                  <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedStudents.includes(student.id)}
                        onChange={() => handleStudentSelect(student.id)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="relative group">
                          {student.profile_picture ? (
                            <img
                              src={student.profile_picture}
                              alt={`${student.lastname} ${student.firstname}`}
                              className="w-10 h-10 rounded-full object-cover cursor-pointer transition-transform group-hover:scale-110"
                              onClick={() => {
                                window.open(student.profile_picture, '_blank');
                              }}
                            />
                          ) : (
                            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                              <User className="w-5 h-5 text-white" />
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                           {student.lastname} {student?.middlename || ''} {student?.firstname || ''} 
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {student.gender || 'N/A'}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-1">
                        <span title="Psychomotors">
                          <Brain 
                            className={`w-4 h-4 cursor-pointer ${Object.values(student.psychomotors || {}).some(v => v === null) ? 'border-red-500 border rounded-full p-0.5' : ''}`} 
                            onClick={() => openModal('psychomotor', [student.id], student)} 
                          />
                        </span>
                        {/* <span title="Offered Subjects">
                          <BookA 
                            className={`w-4 h-4 cursor-pointer ${student.subjects?.length < 9 ? 'border-red-500 border rounded-full p-0.5' : ''}`} 
                            onClick={() => openModal('modify_offered_subjects', [student.id], student)} 
                          />
                        </span> */}
                        <span title="Principal Comments">
                          <MessageCircle 
                            className={`w-4 h-4 cursor-pointer ${!student.principal_comments ? 'border-red-500 border rounded-full p-0.5' : ''}`} 
                            onClick={() => openModal('principal_comment', [student.id], student)} 
                          />
                        </span>
                        <span title="Form Teacher Comments">
                          <MessageSquareDot 
                            className={`w-4 h-4 cursor-pointer ${!student.form_teacher_comments ? 'border-red-500 border rounded-full p-0.5' : ''}`} 
                            onClick={() => openModal('teacher_comment', [student.id], student)} 
                          />
                        </span>
                        {/* <span title="School Post">
                          <GraduationCap 
                            className={`w-4 h-4 cursor-pointer ${!student.post ? 'border-red-500 border rounded-full p-0.5' : ''}`} 
                            onClick={() => openModal('assign_school_post', [student.id], student)} 
                          />
                        </span> */}
                        {/* <span title="Class Post">
                          <AwardIcon 
                            className={`w-4 h-4 cursor-pointer ${!student.class_post ? 'border-red-500 border rounded-full p-0.5' : ''}`} 
                            onClick={() => openModal('assign_class_post', [student.id], student)} 
                          />
                        </span> */}
                        <span title="View Subject Scores">
                          <BookOpen 
                            className="w-4 h-4 cursor-pointer text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300" 
                            onClick={() => {
                              setSelectedStudent(student);
                              setShowSubjectScoresModal(true);
                            }} 
                          />
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {student.admission_no || 'N/A'}
                    </td>

                    {/* This Term Score In Class | Position */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getScoreColor(student.termly_score)}`}>
                        {student.termly_score ?? 'N/A'}
                        {student.termly_position_class ? ` | ${showPositionInActualFormat(student.termly_position_class)}` : ''}
                      </span>
                    </td>

                    {/* This Term Score In Entire ClassLevel | Position */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getScoreColor(student.termly_score)}`}>
                        {student.termly_score ?? 'N/A'}
                        {student.termly_position_level ? ` | ${showPositionInActualFormat(student.termly_position_level)}` : ''}
                      </span>
                    </td>

                    {/* All Terms Score In Class | Position */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getScoreColor(student.final_score)}`}>
                        {student.final_score ?? 'N/A'}
                        {student.final_position_class ? ` | ${showPositionInActualFormat(student.final_position_class)}` : ''}
                      </span>
                    </td>

                    {/* All Terms Score In Entire ClassLevel | Position */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getScoreColor(student.final_score)}`}>
                        {student.final_score ?? 'N/A'}
                        {student.final_position_level ? ` | ${showPositionInActualFormat(student.final_position_level)}` : ''}
                      </span>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        student.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {student.status || 'active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap relative">
                      <button
                        className="p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 action-dropdown"
                        onClick={() => handleActionClick(student.id)}
                      >
                        <MoreVertical className="w-5 h-5" />
                      </button>
                      {actionDropdownId === student.id && (
                        <div className="absolute right-0 mt-2 z-50 w-40 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg action-dropdown">
                          <button 
                            className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" 
                            onClick={() => handleViewDetails(student)}
                          >
                            <Eye className="w-4 h-4 mr-2" /> View Details
                          </button>
                          
                          <button 
                            className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" 
                            onClick={() => handleEditStudent(student.uuid)}
                          >
                            <Pencil className="w-4 h-4 mr-2" /> Edit Student
                          </button>

                          {/* <button 
                            className="w-full flex items-center px-4 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/20" 
                            onClick={() => handleEnterScores(student.uuid)}
                          >
                            <FileText className="w-4 h-4 mr-2" /> Enter Scores
                          </button> */}

                          <button 
                            className="w-full flex items-center px-4 py-2 text-sm text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/20" 
                            onClick={() => handleViewResult(student.uuid)}
                          >
                            <TrendingUp className="w-4 h-4 mr-2" /> View Result
                          </button>
                          {bulkActionOptions.map((action) => (
                            <button
                              key={action.value}
                              onClick={() => openModal(action.value, [student.id], student)}
                              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                              <action.icon className="w-4 h-4 mr-2" /> {action.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Showing {(currentPage - 1) * itemsPerPage + 1}-
            {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} students
          </div>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                className={`px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 ${
                  currentPage === page ? 'bg-blue-600 text-white dark:bg-blue-700' : ''
                }`}
                onClick={() => goToPage(page)}
              >
                {page}
              </button>
            ))}
            <button
              className="px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Batch Results PDF Modal */}
      {showBatchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              Batch Results - {currentClass?.class_name || 'Selected Class'}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              Download PDF for the selected students in this class for the current term and session.
            </p>

            {batchLoading && (
              <div className="flex items-center justify-center py-6">
                <div className="w-10 h-10 border-4 border-blue-500 border-dashed rounded-full animate-spin"></div>
              </div>
            )}

            {!batchLoading && batchError && (
              <div className="mb-4 text-red-600 dark:text-red-400 text-sm">
                {batchError}
              </div>
            )}

            {!batchLoading && !batchError && batchResults.length === 0 && (
              <div className="mb-4 text-gray-600 dark:text-gray-300 text-sm">
                No result data found for the selected students.
              </div>
            )}

            {!batchLoading && batchResults.length > 0 && (
              <div className="flex items-center justify-between mt-2 mb-4">
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {batchResults.length} student{batchResults.length > 1 ? 's' : ''} loaded.
                </span>
                <PDFDownloadLink
                  document={<ClassBatchResultPDF results={batchResults} />}
                  fileName={`class_${currentClass?.class_name || 'results'}_selected_students.pdf`}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  {({ loading }) => (
                    <>
                      {loading ? 'Generating PDF...' : <><FileText className="w-4 h-4" /> Download PDF</>}
                    </>
                  )}
                </PDFDownloadLink>
              </div>
            )}

            <div className="flex justify-end mt-4">
              <button
                onClick={() => {
                  setShowBatchModal(false);
                  setBatchResults([]);
                  setBatchError(null);
                }}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                disabled={batchLoading}
              >
                <FaTimes />
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Student Details Modal */}
      {showDetailsModal && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Student Details
              </h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1">
                  <div className="text-center">
                    <div className="relative inline-block">
                      {selectedStudent.profile_picture ? (
                        <img
                          src={selectedStudent.profile_picture}
                          alt={`${selectedStudent.firstname} ${selectedStudent.lastname}`}
                          className="w-32 h-32 rounded-full object-cover mx-auto border-4 border-blue-100 dark:border-blue-900"
                        />
                      ) : (
                        <div className="w-32 h-32 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto border-4 border-blue-100 dark:border-blue-900">
                          <User className="w-16 h-16 text-white" />
                        </div>
                      )}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mt-4">
                      {selectedStudent.firstname} {selectedStudent.middlename} {selectedStudent.lastname}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">{selectedStudent.admission_no}</p>
                    <div className="mt-4">
                      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                        selectedStudent.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      }`}>
                        {selectedStudent.status}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="lg:col-span-2 space-y-6">
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                      <User className="w-5 h-5 mr-2" />
                      Personal Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Full Name</label>
                        <p className="text-gray-900 dark:text-white">{selectedStudent.firstname} {selectedStudent.middlename} {selectedStudent.lastname}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Gender</label>
                        <p className="text-gray-900 dark:text-white">{selectedStudent.gender || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Date of Birth</label>
                        <p className="text-gray-900 dark:text-white">{formatDate(selectedStudent.date_of_birth)}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Age</label>
                        <p className="text-gray-900 dark:text-white">{calculateAge(selectedStudent.date_of_birth)} years</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Blood Group</label>
                        <p className="text-gray-900 dark:text-white flex items-center">
                          <Droplets className="w-4 h-4 mr-1 text-red-500" />
                          {selectedStudent.blood_group || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Religion</label>
                        <p className="text-gray-900 dark:text-white">{selectedStudent.religion || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                      <BookOpen className="w-5 h-5 mr-2" />
                      Academic Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Current Class</label>
                        <p className="text-gray-900 dark:text-white">{selectedStudent.classroom_name || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Admission Date</label>
                        <p className="text-gray-900 dark:text-white">{formatDate(selectedStudent.admission_date)}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Previous School</label>
                        <p className="text-gray-900 dark:text-white">{selectedStudent.previous_school || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">In Current Term</label>
                        <p className="text-gray-900 dark:text-white flex items-center">
                          <UserCheck className="w-4 h-4 mr-1 text-green-500" />
                          {selectedStudent.in_current_term === 'yes' ? 'Yes' : 'No'}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                      <Mail className="w-5 h-5 mr-2" />
                      Contact Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Student Email</label>
                        <p className="text-gray-900 dark:text-white flex items-center">
                          <Mail className="w-4 h-4 mr-1" />
                          {selectedStudent.student_email || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Student Phone</label>
                        <p className="text-gray-900 dark:text-white flex items-center">
                          <Phone className="w-4 h-4 mr-1" />
                          {selectedStudent.student_phone || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Parent Name</label>
                        <p className="text-gray-900 dark:text-white">{selectedStudent.parent_name || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Parent Phone</label>
                        <p className="text-gray-900 dark:text-white flex items-center">
                          <Phone className="w-4 h-4 mr-1" />
                          {selectedStudent.parent_phone || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Parent Email</label>
                        <p className="text-gray-900 dark:text-white flex items-center">
                          <Mail className="w-4 h-4 mr-1" />
                          {selectedStudent.parent_email || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Emergency Phone</label>
                        <p className="text-gray-900 dark:text-white flex items-center">
                          <Phone className="w-4 h-4 mr-1" />
                          {selectedStudent.emergency_phone || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                      <MapPin className="w-5 h-5 mr-2" />
                      Address & Additional Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Address</label>
                        <p className="text-gray-900 dark:text-white">{selectedStudent.address || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">State of Origin</label>
                        <p className="text-gray-900 dark:text-white">{selectedStudent.state_of_origin || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">LGA</label>
                        <p className="text-gray-900 dark:text-white">{selectedStudent.lga || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Nationality</label>
                        <p className="text-gray-900 dark:text-white">{selectedStudent.nationality || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Allergies</label>
                        <p className="text-gray-900 dark:text-white flex items-center">
                          <Shield className="w-4 h-4 mr-1 text-orange-500" />
                          {selectedStudent.allergies || 'None'}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600 dark:text-gray-400">Special Needs</label>
                        <p className="text-gray-900 dark:text-white">{selectedStudent.special_needs || 'None'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              >
                Close
              </button>

              {/* <button
                onClick={() => {
                  setShowDetailsModal(false);
                  handleEnterScores(selectedStudent.uuid);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Enter Scores
              </button> */}

            </div>
          </div>
        </div>
      )}

      {/* Action Modal */}
      {showActionModal && (
        <ActionModal
          action={selectedAction}
          studentIds={modalData.studentIds}
          onSubmit={handleSubmit}
          onClose={closeModal}
          psychomotorSkills={psychomotorSkills}
          classOptions={classOptions}
          currentClass={currentClass?.class_name}
          initialData={modalData.initialData}
        />
      )}

      {/* Subject Selection Modal */}
      {isSubjectModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={closeSubjectModal}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Select Subject</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Choose a subject to enter results for <span className="font-semibold">{selectedClass}</span>.
            </p>
            <div className="space-y-4">
              <Select
                options={subjectOptions}
                value={selectedSubject}
                onChange={setSelectedSubject}
                placeholder="Select a subject"
                isSearchable
                styles={customSelectStyles}
                className="react-select-container"
                classNamePrefix="react-select"
              />
              <div className="flex justify-end gap-4">
                <button
                  onClick={closeSubjectModal}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <FaTimes />
                  Close
                </button>
                <button
                  onClick={handleSubjectSubmit}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <FaFileAlt />
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subject Scores Modal */}
      {showSubjectScoresModal && selectedStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Subject Scores - {selectedStudent.lastname} {selectedStudent.firstname || ''} {selectedStudent.middlename || ''}
              </h2>
              <button
                onClick={() => {
                  setShowSubjectScoresModal(false);
                  setSelectedStudent(null);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Current Term ({currentTerm}) - {currentClass?.class_name}
                </h3>
                {selectedStudent.subject_scores && selectedStudent.subject_scores.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">Subject</th>
                          <th className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">Score</th>
                          <th className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">Grade</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {selectedStudent.subject_scores.map((subject, idx) => (
                          <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{subject.subject_name}</td>
                            {/* <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{parseFloat(subject.total_score || 0).toFixed(1)}</td> */}
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{formatScore(subject.total_score || 0)}</td>
                            <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                subject.grade === 'A' ? 'bg-green-100 text-green-800' :
                                subject.grade === 'B' ? 'bg-blue-100 text-blue-800' :
                                subject.grade === 'C' ? 'bg-yellow-100 text-yellow-800' :
                                subject.grade === 'D' ? 'bg-orange-100 text-orange-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {subject.grade || 'N/A'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
                      <p><strong>Term Average:</strong> {
                        (selectedStudent.subject_scores.reduce((sum, s) => sum + (parseFloat(s.total_score) || 0), 0) / selectedStudent.subject_scores.length).toFixed(1)
                      }</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400">No subject scores available for this term.</p>
                )}
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Cumulative (All Terms) - {currentClass?.class_name}
                </h3>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {/* <strong>Termly Score:</strong> {selectedStudent.termly_score ? parseFloat(selectedStudent.termly_score).toFixed(1) : 'N/A'} */}
                    <strong>Termly Score:</strong> {selectedStudent.termly_score ? formatScore(selectedStudent.termly_score) : 'N/A'}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {/* <strong>Cumulative Score:</strong> {selectedStudent.final_score ? parseFloat(selectedStudent.final_score).toFixed(1) : 'N/A'} */}
                    <strong>Cumulative Score:</strong> {selectedStudent.final_score ? formatScore(selectedStudent.final_score) : 'N/A'}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    <strong>Termly Position:</strong> {selectedStudent.termly_position_class ? `${selectedStudent.termly_position_class}${selectedStudent.termly_position_class === 1 ? 'st' : selectedStudent.termly_position_class === 2 ? 'nd' : selectedStudent.termly_position_class === 3 ? 'rd' : 'th'}` : 'N/A'}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    <strong>Cumulative Position:</strong> {selectedStudent.final_position_class ? `${selectedStudent.final_position_class}${selectedStudent.final_position_class === 1 ? 'st' : selectedStudent.final_position_class === 2 ? 'nd' : selectedStudent.final_position_class === 3 ? 'rd' : 'th'}` : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowSubjectScoresModal(false);
                  setSelectedStudent(null);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Students;